import InventoryService from "../InventoryService";
import BranchService from "@/modules/setup/branch/BranchService";
import i18n from "../../../shared/i18n";
import SalesService from "../../sales/SalesService";
import {Formatter} from "@/core/services/formatter.service";
import {
  INVENTORY_TABLE_NAME,
  SOURCE_INVENTORY_DASHBOARD,
  SOURCE_INVENTORY_TABLE,
  SOURCE_RECEIPT
} from "../../constants";
import InventorySearch from "../InventorySearch.model";
import {store} from "@/plugins/vuex";
import cloneDeep from "lodash/cloneDeep";
import {isSettingEnabled} from "@/core/directives/setting-enabled";
import _ from "lodash";

let getDefaultTableData = (differentFields = []) => {
  let data = {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new InventorySearch(),
    extraFilters: {},
    expand: 'branch,reservations.auction.dates,available_quantity',
    toggleColumns: {
      fields: [...commonFields(), ...differentFields],
      requiredFields: ['stock_no', 'reserve'],
      defaultFields: ['stock_no', 'image', 'title', 'low', 'quantity', 'reserve', 'receive_date', 'status'],
      dynamicFields: [],
      exportFieldsMap: {
        'status': 'statuses[[display_name]]',
        'title': 'dynamic_fields.{{masterLanguage}}.title',
        'tax_status': 'tax_status.name',
        'item_location': 'item_location.name',
        'cataloguing_lifecycle': 'cataloguing_lifecycle',
        'photography_lifecycle': 'photography_lifecycle',
        'customer': 'customer.display.name',
        'account_managers': 'account_managers[[display_name]]',
        'department': 'department.name.{{masterLanguage}}',
        'category': 'category.name.{{masterLanguage}}',
      },
      excludeExportFields: ['image']
    }
  }

  if (isSettingEnabled('isSalesTaxEnabled')) {
    data.toggleColumns.fields = data.toggleColumns.fields.filter(f => !['tax_status', 'tax_rate'].includes(f.key))
  }

  return data
};

let commonFields = () => {
  return [
    {
      key: 'stock_no',
      label: i18n.t('Receipt / Item'),
      sortable: true,
      tdClass: "stock-no-col",
      isVisible: true,
      expand: 'receipt,source_inventories_count,grouped_inventories_count'
    },
    {
      key: 'image',
      label: i18n.t('Image'),
      sortable: false,
      tdClass: 'thumbnail-img-holder',
      isVisible: true,
      expand: 'images',
      exclude_from_export: true
    },
    {key: 'title', label: i18n.t('Title'), sortable: true, tdClass: 'title-col', isVisible: true, expand: ''},
    {key: 'low', label: i18n.t('Estimate'), sortable: true, tdClass: 'estimate-col', isVisible: true, expand: ''},
    {key: 'quantity', label: i18n.t('Quantity'), sortable: true, tdClass: 'quantity-col', isVisible: true, expand: ''},
    {key: 'reserve', label: i18n.t('Reserve'), sortable: false, tdClass: 'reserve-col', isVisible: true, expand: ''},
    {
      key: 'receive_date',
      label: i18n.t('Receive Date'),
      sortable: true,
      tdClass: 'receive-date-col',
      isVisible: true,
      expand: ''
    },
    {
      key: 'tax_status',
      label: i18n.t('Tax Status'),
      sortable: true,
      tdClass: 'tax-status-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'tax_rate',
      label: i18n.t('Tax Rate'),
      sortable: true,
      tdClass: 'tax-rate-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'department',
      label: i18n.t('Department'),
      sortable: true,
      tdClass: 'department-col',
      isVisible: false,
      expand: 'department'
    },
    {
      key: 'category',
      label: i18n.t('Category'),
      sortable: true,
      tdClass: 'category-col',
      isVisible: false,
      expand: 'category'
    },
    {
      key: 'item_location',
      label: i18n.t('Item Location'),
      sortable: true,
      tdClass: 'item-location-col',
      isVisible: false,
      expand: 'item_location,movement_history_count'
    },
    {
      key: 'cataloguing_lifecycle',
      label: i18n.t('Cataloguing Lifecycle'),
      sortable: false,
      tdClass: 'cataloguing-lifecycle-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'photography_lifecycle',
      label: i18n.t('Photography Lifecycle'),
      sortable: false,
      tdClass: 'photography-lifecycle-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'start_price',
      label: i18n.t('Start Price'),
      sortable: true,
      tdClass: 'start-price-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'collection_info',
      label: i18n.t('Collection Information'),
      sortable: false,
      tdClass: 'collection-info-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'created_at',
      label: i18n.t('Created at / Created by'),
      sortable: true,
      tdClass: 'created-data-col',
      isVisible: false,
      expand: 'created_by'
    },
    {
      key: 'updated_at',
      label: i18n.t('Updated at / Updated by'),
      sortable: true,
      tdClass: 'updated-data-col',
      isVisible: false,
      expand: 'updated_by'
    },
    {
      key: 'status',
      label: i18n.t('Status'),
      sortable: false,
      tdClass: "status-col",
      isVisible: true,
      expand: 'statuses'
    },
    {
      key: "account_managers",
      label: i18n.t('Account Manager'),
      sortable: false,
      tdClass: "account-managers-col",
      expand: 'account_managers',
      isVisible: false,
    },
    {
      key: "available_quantity",
      label: i18n.t('Available Quantity'),
      sortable: false,
      tdClass: "available_quantity-col",
      expand: 'available_quantity',
      isVisible: false,
    },
    {
      key: "company_name",
      label: i18n.t('Company Name'),
      sortable: true,
      tdClass: "company-name-col",
      expand: 'customer',
      isVisible: false,
    },
    {
      key: "grouped_item_no",
      label: i18n.t('Group Item No'),
      sortable: true,
      tdClass: "grouped_item_no-col",
      expand: 'grouped_inventories.grouped_inventory',
      isVisible: false,
      exclude_from_export: true
    },
    {
      key: "collected",
      label: i18n.t('Collected'),
      sortable: true,
      tdClass: "collected-col",
      expand: '',
      isVisible: false,
    },
    {
      key: "collected_status",
      label: i18n.t('Collected Status'),
      sortable: false,
      tdClass: "collected-status-col",
      expand: 'collected_status',
      isVisible: false,
    },
    {
      key: "lot_no",
      label: i18n.t('Lot'),
      sortable: false,
      tdClass: 'lot-no-col',
      isVisible: false,
      expand: 'latest_reservation'
    },
    {
      key: "hammer_price",
      label: i18n.t('Hammer Price'),
      sortable: false,
      tdClass: 'lot-no-col',
      isVisible: false,
      expand: 'latest_reservation'
    },
    {
      key: "reserve_type",
      label: i18n.t('Reserve Type'),
      sortable: true,
      tdClass: 'reserve-type-col',
      isVisible: false,
      expand: ''
    },
    {
      key: "specialists",
      label: i18n.t('Specialists'),
      sortable: false,
      tdClass: 'specialists-col',
      isVisible: false,
      expand: 'specialists'
    },
    {
      key: "return_note_collector",
      label: i18n.t('Collector'),
      sortable: false,
      tdClass: 'specialists-col',
      isVisible: false,
      expand: 'return_notes'
    },
    {
      key: "condition_requests_count",
      label: i18n.t('Condition Requests Count'),
      sortable: true,
      tdClass: 'condition_request_count-col',
      isVisible: false,
      expand: 'condition_requests_count'
    },
    {
      key: "condition_requests_sent",
      label: i18n.t('Condition Requests Sent'),
      sortable: true,
      tdClass: 'condition_request_sent-col',
      isVisible: false,
      expand: 'condition_requests_sent'
    },
    {
      key: "dpv_pdf",
      label: i18n.t('DPV PDF'),
      sortable: false,
      isVisible: false,
      exclude_from_export: true,
      expand: 'dpv_pdf'
    },
    {
      key: "proposal_suggestion",
      label: i18n.t('Proposal Suggestion'),
      sortable: false,
      tdClass: 'proposal-suggestion-col',
      isVisible: false,
      expand: 'latest_reservation'
    },
    {
      key: "proposal_status",
      label: i18n.t('Proposal Status'),
      sortable: false,
      tdClass: 'proposal-status-col',
      isVisible: false,
      expand: 'latest_reservation'
    },
    {
      key: "transfer_info",
      label: i18n.t('Transfer Info'),
      sortable: false,
      tdClass: 'transfer_status-col',
      isVisible: false,
      expand: 'latest_transfer_note_info'
    },
    {
      key: "rep_user",
      label: i18n.t('Representative User'),
      sortable: false,
      tdClass: 'rep-user-col',
      isVisible: false,
      expand: 'rep_user'
    },
    {
      key: "internal_images",
      label: i18n.t('Internal Images'),
      sortable: false,
      tdClass: 'internal-images-col',
      isVisible: false,
      expand: 'internal_images'
    },
    {
      key: "attachments",
      label: i18n.t('Attachments'),
      sortable: false,
      tdClass: 'attachments-col',
      isVisible: false,
      expand: 'attachments'
    },
  ]
};

let stockInventoryFields = () => {
  return [
    {
      key: 'customer',
      label: i18n.t('Customer'),
      sortable: true,
      tdClass: 'customer-col',
      isVisible: false,
      expand: 'customer'
    },
  ]
};

export default {
  namespaced: true,
  state: {
    statuses: [
      {value: 'draft', key: i18n.t('Draft'),},
      {value: 'lotted', key: i18n.t('Lotted'),},
      {value: 'sold', key: i18n.t('Sold'),},
      {value: 'withdrawn', key: i18n.t('Withdrawn')},
    ],
    modal: {
      error: null,
      loading: false,
      show: false,
      object: null,
      source: null,
    },
    qrGenerateModal: {
      show: false,
      source: null
    },
    inventoryStatusesModal: {
      inventory: null,
      show: false,
      statusKey: 'statuses',
      source: null
    },
    reserveSaleModal: {
      show: false,
      items: [],
      sale: null,
      source: null
    },
    inventoryOverviewGenerationModal: {
      show: false,
      source: null,
      payload: {}
    },
    groupedItemsReserveModal: {
      show: false,
      items: [],
      source: null
    },
    groupItemsModal: {
      show: false,
      source: null
    },
    inventoryMiscChargeModal: {
      show: false,
      object: null,
      source: null
    },
    tableSource: null,
    tableData: {
      inventory: getDefaultTableData(stockInventoryFields()),
      inventorySourceInventories: getDefaultTableData(stockInventoryFields()),
      receipt: getDefaultTableData(),
      reserveItemsModal: getDefaultTableData(stockInventoryFields())
    },
    view: {
      routeFrom: null,
      loading: false,
      currentInventory: null,
      images: [],
      deletedImagesUuids: [],
      commissionsModal: {
        inventory: null,
        show: false,
        object: null,
        source: null
      },
      taxRateModal: {
        inventory: null,
        show: false,
        object: null,
        source: null
      },
      attachmentsCurrentTab: 0
    },
    conditionalReportRequestModal: {
      show: false,
      reservations: [],
      object: {customer_uuid: null, customer: null}
    },
    googleMapsModal: {
      show: false,
      params: {},
    },
    continuousAuctionBatchLotUpModal: {
      show: false,
      source: null,
      loading: false,
      items: [],
    },
    bulkEditModal: {
      error: null,
      show: false,
      source: null,
      loading: false,
      items: [],
    },
    transferAcrossBranchesModal: {
      show: false,
      loading: false,
      items: [],
      branches: [],
      branchUuidItemLocationsMap: {},
    },
    batchUpdateReceiptModal: {
      show: false,
      loading: false,
      items: [],
    },
  },
  getters: {
    toggleColumns: state => {
      const data = state.tableData[state.tableSource].toggleColumns
      data.tableName = `${INVENTORY_TABLE_NAME}.${state.tableSource}`
      return data
    },
    tableFields: (state, getters) => {
      return getters.toggleColumns.fields.filter(f => f.isVisible)
    },
  },
  actions: {
    initTableData({state}) {
      state.tableData.inventory = getDefaultTableData(stockInventoryFields());
      state.tableData.inventorySourceInventories = getDefaultTableData(stockInventoryFields());
      state.tableData.receipt = getDefaultTableData();
      state.tableData.reserveItemsModal = getDefaultTableData(stockInventoryFields());
    },
    async getModalObject({commit, state, rootState}, {uuid, source, isDuplicate = false}) {
      state.modal.loading = true;
      let expands = [
        "reservations.auction.dates",
        "category.dynamic_fields_template",
        "department",
        "account_managers",
        "branch",
        "receipt.customer",
        "customer.shipping_address",
        "expert_user",
        "rep_user",
        "statuses",
        "financial_items.invoice",
        "grouped_inventories.grouped_inventory.reservations",
        "grouped_inventories.grouped_inventory.financial_items",
        "source_inventories.source_inventory",
        "specialists",
        "lifecycle_last_updated"
      ]

      if (rootState.global.enableInventoryDraft) {
        expands.push('published_at_least_once')
      }

      let response = await InventoryService.getInventory(uuid, expands.join(','));

      if (response.success) {
        let object = cloneDeep(response.body)
        object.specialists_uuids = []
        if (object.specialists && object.specialists.length > 0) {
          object.specialists_uuids = object.specialists.map(s => s.uuid)
        }

        if (isDuplicate) {
          delete object.uuid;
          delete object.item_no;
          object.collected = false;
          object.reservations = [];
          object.financial_items = [];
          object.source_inventories = object.source_inventories.map(s => {
            return {
              source_inventory_uuid: s.source_inventory_uuid,
              source_inventory: s.source_inventory,
              quantity: s.quantity,
            }
          });
          object.isDuplicate = true;
        }
        commit('updateModalObject', {object: object, source})
      } else {
        state.modal.error = response.toastError;
      }
      state.modal.loading = false;
    },
    async showModal({commit}, {object = null, source} = {}) {
      commit('showModal', {object: cloneDeep(object), source});
    },
    async showQRGenerateModal({commit}, {source} = {}) {
      commit('showQRGenerateModal', {source});
    },
    async hideModal({commit}) {
      commit('hideModal');
    },
    async hideQRGenerateModal({commit}) {
      commit('hideQRGenerateModal');
    },
    async showReserveSaleModal({commit}, {items = null, source, sale} = {}) {
      commit('showReserveSaleModal', {items: cloneDeep(items), source, sale});
    },
    async hideReserveSaleModal({commit}) {
      commit('hideReserveSaleModal');
    },
    async showGroupedItemsReserveModal({commit}, {items = null, source} = {}) {
      commit('showGroupedItemsReserveModal', {items: cloneDeep(items), source});
    },
    async hideGroupedItemsReserveModal({commit}) {
      commit('hideGroupedItemsReserveModal');
    },
    async showGroupItemsModal({commit}, {source} = {}) {
      commit('showGroupItemsModal', {source});
    },
    async hideGroupItemsModal({commit}) {
      commit('hideGroupItemsModal');
    },
    resetTableDataPaginationPage({state}) {
      state.tableData[state.tableSource].pagination.page = 1;
    },
    updateSearchModel({commit, state}, searchModel) {
      commit('updateSearchModel', new InventorySearch({
        ...state.tableData[state.tableSource].searchModel,
        ...searchModel
      }));
    },
    resetSearchModel({commit}) {
      commit('resetSearchModel')
    },
    async resetTable({state, dispatch}) {
      const extraFilters = state.tableData[state.tableSource].extraFilters;
      const tmpToggleColumns = cloneDeep(state.tableData[state.tableSource].toggleColumns);
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData[state.tableSource].pagination.limit;
      state.tableData[state.tableSource] = defaultTableData;
      state.tableData[state.tableSource].toggleColumns = tmpToggleColumns;
      state.tableData[state.tableSource].extraFilters = extraFilters;
      dispatch('getTableData');
    },
    async getTableData({commit, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      commit('updateTableLoading', true);
      const response = await InventoryService.getInventories(params);
      commit('updateTableLoading', false);

      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }

      return response;
    },
    updateInventoryTablePage({state}, page) {
      state.tableData[state.tableSource].pagination.page = page;
    },
    async getCurrentExpands({state}) {
      let currentTableData = state.tableData[state.tableSource]

      if (!currentTableData) {
        return ''
      }

      const defaultExpand = currentTableData.expand
      const currentExpand = currentTableData.toggleColumns.fields
        .filter(c => currentTableData.toggleColumns.defaultFields.includes(c.key) && c.expand && !defaultExpand.includes(c.expand))
        .map(c => c.expand)
        .join(',')

      return `${currentExpand},${defaultExpand}`
    },
    async getTableParams({state, dispatch}, payload = null) {
      let data;
      let currentTableData = state.tableData[state.tableSource];
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: currentTableData,
        tableName: `${INVENTORY_TABLE_NAME}.${state.tableSource}`
      });

      if (payload) {
        data = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        data = {
          ...currentTableData.pagination,
          search: currentTableData.search,
          ordering: ordering,
        }
      }

      if (currentTableData.searchModel) {
        const searchModelData = currentTableData.searchModel.toJSON();
        data = {
          ...data,
          ...searchModelData
        }
      }

      if (currentTableData.extraFilters) {
        data = {...data, ...currentTableData.extraFilters};
      }

      data.expand = await dispatch('getCurrentExpands');

      return data;
    },
    async updateRouteFrom({state}, payload) {
      state.view.routeFrom = payload
    },
    async getUpcomingSales({commit}) {
      let response = await SalesService.getSales({limit: 50, start_date: Formatter.getNowAsDate()});

      if (response.success) {
        commit('updateUpcomingSales', response.body.results);
      }

      return response;
    },
    createInventory({commit}, payload) {
      return InventoryService.createInventory(payload, 'reservations.auction');
    },
    createGroupedInventory({commit}, payload) {
      return InventoryService.createGroupedInventory(payload, 'reservations.auction');
    },
    async updateInventory({commit, state, dispatch}, inventory) {
      let expand = [
        'reservations.auction',
        'branch',
        'receipt.customer',
        'department',
        'customer.billing_address',
        'customer.shipping_address',
        'available_quantity',
        'item_location',
        'category',
        'statuses',
        'account_managers',
        'specialists',
        'latest_reservation'
      ].join(',');

      if (state.modal.source === SOURCE_INVENTORY_TABLE) {
        expand = await dispatch('getCurrentExpands');
      }

      let response = await InventoryService.updateInventory(inventory.uuid, inventory, expand);

      if (response.success && state.modal.source === SOURCE_INVENTORY_TABLE) {
        commit('updateInventory', response.body);
        if ([SOURCE_RECEIPT].includes(state.tableSource)) {
          dispatch('getTableData');
        }
      }
      return response;
    },
    async updateGroupedInventory({commit, state, dispatch}, inventory) {
      let expand = [
        'reservations.auction',
        'branch',
        'receipt.customer',
        'department',
        'customer.billing_address',
        'customer.shipping_address',
        'available_quantity',
        'item_location',
        'category',
        'statuses',
        'account_managers',
        'specialists',
        'latest_reservation'
      ].join(',');

      if (state.modal.source === SOURCE_INVENTORY_TABLE) {
        expand = await dispatch('getCurrentExpands');
      }

      let response = await InventoryService.updateGroupedInventory(inventory.uuid, inventory, expand);
      if (response.success && state.modal.source === SOURCE_INVENTORY_TABLE) {
        commit('updateInventory', response.body);
        if ([SOURCE_RECEIPT].includes(state.tableSource)) {
          dispatch('getTableData');
        }
      }
      return response;
    },
    async updateListInventoryImages({commit}, inventory) {
      commit('updateInventory', inventory);
    },
    async removeListInventoryImagesByUuids({commit}, payload) {
      commit('removeListInventoryImagesByUuids', payload);
    },
    async removeInventory({dispatch}, inventory) {
      let response = await InventoryService.removeInventory(inventory.uuid);
      if (response.success) {
        dispatch('getTableData')
      }
      return response;
    },
    async removeSourceItem({dispatch}, {grouped_inventory_uuid, source_inventory_uuid}) {
      let response = await InventoryService.removeSourceInventory({
        grouped_inventory_uuid, source_inventory_uuid
      });
      if (response.success) {
        dispatch('getTableData')
      }
      return response;
    },
    async getInventory({commit}, payload) {
      const uuid = typeof payload === 'string' ? payload : payload && payload.uuid;
      const silent = typeof payload === 'object' && !!payload.silent;

      if (!silent) {
        commit('setCurrentInventoryLoading', true);
      }
      let expand = [
        'available_quantity',
        'reservations.auction',
        'branch',
        'receipt.customer',
        'receipt.department',
        'department',
        'customer.billing_address',
        'customer.shipping_address',
        'item_location',
        'movement_history_count',
        'category',
        'statuses',
        'customer.logo',
        'source_inventories_count',
        'grouped_inventories_count',
        'grouped_inventories.grouped_inventory',
        'inventory_miscellaneous_items',
        'movement_histories',
        'movement_histories.from_location_path',
        'movement_histories.to_location_path',
        'specialists',
        'receipt.commissions',
        'latest_reservation',
        'financial_items.invoice',
        'collected_status',
        'latest_transfer_note_info',
      ].join(',')
      try {
        let response = await InventoryService.getInventory(uuid, expand);
        if (response.success) {
          commit('updateViewInventory', response.body);
        }
        return response;
      } finally {
        if (!silent) {
          commit('setCurrentInventoryLoading', false);
        }
      }
    },
    async updateInventoryItemLocation({state}, {uuid, item_location_uuid}) {
      let response = await InventoryService.partialUpdateInventory(uuid, {item_location_uuid}, 'item_location');

      if (response.success) {
        state.view.currentInventory = {
          ...state.view.currentInventory,
          ...response.body
        }
      }

      return response;
    },
    async updateInventoryDashboardCustomer({commit}, payload) {
      commit('updateInventoryDashboardCustomer', payload);
    },
    async updateViewInventory({commit}, data) {
      commit('updateViewInventory', data);
    },
    updateDeletedImages({state}, deletedImagesUuids) {
      state.view.deletedImagesUuids = deletedImagesUuids
    },
    async getImages({commit, state}, uuid) {
      let response = await InventoryService.getImages(uuid);

      if (response.success) {
        commit('updateImages', response.body.filter(i => !state.view.deletedImagesUuids.includes(i.uuid)));
      }

      return response;
    },
    async updateImages({commit}, payload) {
      await commit('updateImages', payload);
    },
    async updateTableSource({state}, {extraFilters, source}) {
      state.tableSource = source;
      state.tableData[source].extraFilters = extraFilters;
    },
    async batchRemoveItems({dispatch}, payload) {
      return await InventoryService.batchRemoveItems(payload);
    },
    async batchArchiveItems({dispatch}, payload) {
      return await InventoryService.batchArchiveItems(payload);
    },
    async batchUnarchiveItems({dispatch}, payload) {
      return await InventoryService.batchUnarchiveItems(payload);
    },
    showGoogleMapsModal({commit}, payload = null) {
      commit('showGoogleMapsModal', payload)
    },
    hideGoogleMapsModal({commit}) {
      commit('hideGoogleMapsModal')
    },
    async getLongAndLat() {
      let params = {};
      try {
        const {coords} = await InventoryService.getCurrentPosition();
        params = {
          lat: parseFloat(coords.latitude),
          lng: parseFloat(coords.longitude)
        };
      } catch (e) {
        console.error(e.message);
        params = {lng: 0, lat: 0};
      }
      return params;
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    },
    showContinuousAuctionBatchLotUpModal({commit}, payload) {
      commit('showContinuousAuctionBatchLotUpModal', payload);
    },
    hideContinuousAuctionBatchLotUpModal({commit}) {
      commit('hideContinuousAuctionBatchLotUpModal');
    },
    toggleContinuousAuctionBatchLotUpModalLoading({commit}, payload) {
      commit('toggleContinuousAuctionBatchLotUpModalLoading', payload);
    },
    async getDataForContinuousAuctionBatchLotUpModal({commit}, uuids) {
      commit("toggleContinuousAuctionBatchLotUpModalLoading", true);
      let response = await InventoryService.getInventories({
        limit: 200,
        uuid: uuids.join(','),
        expand: 'reservations,available_quantity'
      });

      if (response.success) {
        commit('getDataForContinuousAuctionBatchLotUpModal', response.body.results);
      }

      commit("toggleContinuousAuctionBatchLotUpModalLoading", false);
      return response;
    },
    async showBulkEditModal({commit, state}, {source, uuids} = {}) {
      state.bulkEditModal.loading = true;
      await commit('showBulkEditModal', {source});
      let expands = [
        'account_managers',
        'rep_user',
        'tax_status',
        'reservations.auction.dates',
        'expert_user',
        'financial_items.invoice',
        'item_location',
      ]
      let response = await InventoryService.getInventories({
        limit: 200,
        uuid: uuids.join(','),
        expand: expands.join(',')
      });

      if (response.success) {
        state.bulkEditModal.items = response.body.results
      }
      state.bulkEditModal.loading = false;
    },
    hideBulkEditModal({commit}) {
      commit('hideBulkEditModal');
    },
    async inventoryBulkUpdate({state}, payload) {
      state.bulkEditModal.loading = true
      let response = await InventoryService.inventoryBulkUpdate(payload);
      state.bulkEditModal.loading = false
      return response
    },
    showInventoryMiscChargeModal({commit}, {object = {}, source = null}) {
      commit('showInventoryMiscChargeModal', {object: cloneDeep(object), source})
    },
    hideInventoryMiscChargeModal({commit}) {
      commit('hideInventoryMiscChargeModal')
    },
    async createInventoryMiscCharge({state}, payload) {
      let response = await InventoryService.createInventoryMiscCharge(payload.inventory_uuid, payload)
      if (response.success) {
        if (state.inventoryMiscChargeModal.source === SOURCE_INVENTORY_DASHBOARD) {
          state.view.currentInventory.inventory_miscellaneous_items.push(response.body)
        }
      }
      return response
    },
    async updateInventoryMiscCharge({state}, payload) {
      let response = await InventoryService.updateInventoryMiscCharge(payload.inventory_uuid, payload.uuid, payload)
      if (response.success) {
        if (state.inventoryMiscChargeModal.source === SOURCE_INVENTORY_DASHBOARD) {
          const index = state.view.currentInventory.inventory_miscellaneous_items.findIndex(i => i.uuid === payload.uuid);
          state.view.currentInventory.inventory_miscellaneous_items[index] = {
            ...state.view.currentInventory.inventory_miscellaneous_items[index],
            ...response.body
          };
          state.view.currentInventory.inventory_miscellaneous_items = [...state.view.currentInventory.inventory_miscellaneous_items]
        }
      }
      return response
    },
    async deleteInventoryMiscCharge({state}, payload) {
      let response = await InventoryService.removeInventoryMiscCharge(payload.inventory_uuid, payload.uuid)
      if (response.success) {
        const index = state.view.currentInventory.inventory_miscellaneous_items.findIndex(i => i.uuid === payload.uuid);
        state.view.currentInventory.inventory_miscellaneous_items.splice(index, 1)
      }
      return response
    },
    async showInventoryStatusesModal({commit}, {inventory = null, source, statusKey='statuses'} = {}) {
      commit('showInventoryStatusesModal', {inventory: cloneDeep(inventory), source, statusKey});
    },
    async hideInventoryStatusesModal({commit}) {
      commit('hideInventoryStatusesModal');
    },
    async showInventoryOverviewGenerationModal({commit}, {payload = {}, source = null} = {}) {
      commit('showInventoryOverviewGenerationModal', {payload, source});
    },
    async hideInventoryOverviewGenerationModal({commit}) {
      commit('hideInventoryOverviewGenerationModal');
    },
    async showConditionalReportRequestModal({commit}, {reservations = []}) {
      commit('showConditionalReportRequestModal', {reservations})
    },
    async hideConditionalReportRequestModal({commit}) {
      commit('hideConditionalReportRequestModal')
    },
    updateConditionalReportRequestModalCustomer({commit}, customer) {
      commit('updateConditionalReportRequestModalCustomer', customer);
    },
    async showCommissionsModal({commit}, {object = {}, source = null, inventory = null} = {}) {
      commit('showCommissionsModal', {object, source, inventory: cloneDeep(inventory)});
    },
    async showTaxRateModal({commit}, {object = {}, source = null, inventory = null} = {}) {
      commit('showTaxRateModal', {object, source, inventory: cloneDeep(inventory)});
    },
    async hideCommissionsModal({commit}) {
      commit('hideCommissionsModal');
    },
    async hideTaxRateModal({commit}) {
      commit('hideTaxRateModal');
    },
    async updateInventoryTerms({state}, payload) {
      let response = await InventoryService.updateInventoryTerms(payload)
      if (response.success) {
        if (payload.data.tax_rate) {
          state.view.currentInventory.tax_rate = payload.data.tax_rate;
        } else {
          state.view.currentInventory.commission_tiers = payload.data.commission_tiers;
          state.view.currentInventory.minimum_commission = payload.data.minimum_commission;
          state.view.currentInventory.insurance_rate = payload.data.insurance_rate;
          state.view.currentInventory.lot_handling_charge = payload.data.lot_handling_charge;
          state.view.currentInventory.rebate_rate = payload.data.rebate_rate;
        }
      }

      return response
    },
    async showTransferAcrossBranchesModal({commit, state}, {uuids, branchUuids, customerUuid}) {
      state.transferAcrossBranchesModal.loading = true;
      commit('showTransferAcrossBranchesModal');

      const [itemsRes, branchesRes] = await Promise.all([
        InventoryService.getInventories({
          limit: 200,
          uuid: uuids.join(','),
          expand: 'department,category,description',
        }),
        BranchService.getBranches({
          uuid: branchUuids.join(','),
          expand: 'item_locations',
        }),
      ]);

      if (itemsRes.success && branchesRes.success) {
        state.transferAcrossBranchesModal.items = itemsRes.body.results;
        state.transferAcrossBranchesModal.branches = branchesRes.body;

        state.transferAcrossBranchesModal.branchUuidItemLocationsMap = branchesRes.body.reduce((acc, item) => {
          acc[item.uuid] = item.item_locations;
          return acc;
        }, {});
        state.transferAcrossBranchesModal.loading = false;
      }
    },
    hideTransferAcrossBranchesModal({commit}) {
      commit('hideTransferAcrossBranchesModal');
    },
    async showBatchUpdateReceiptModal({commit, state}, {uuids}) {
      state.batchUpdateReceiptModal.loading = true;
      commit('showBatchUpdateReceiptModal');

      const itemsRes = await InventoryService.getInventories({
        limit: 200,
        uuid: uuids.join(','),
        expand: 'department,category,description,item_location,tax_status',
      });

      if (itemsRes.success) {
        state.batchUpdateReceiptModal.items = itemsRes.body.results;
        state.batchUpdateReceiptModal.loading = false;
      }
    },
    hideBatchUpdateReceiptModal({commit}) {
      commit('hideBatchUpdateReceiptModal');
    },
    updateAttachmentsCurrentTab({state}, value) {
      state.view.attachmentsCurrentTab = value;
      return true
    }
  },
  mutations: {
    showModal: (state, {object, source}) => {
      state.modal.show = true;
      state.modal.object = object;
      state.modal.source = source;
    },
    showReserveSaleModal: (state, {items, source, sale}) => {
      state.reserveSaleModal.show = true;
      state.reserveSaleModal.items = items;
      state.reserveSaleModal.source = source;
      state.reserveSaleModal.sale = sale;
    },
    showQRGenerateModal: (state, {source}) => {
      state.qrGenerateModal.show = true;
      state.qrGenerateModal.source = source;
    },
    updateModalObject: (state, {object, source}) => {
      state.modal.object = object;
      state.modal.source = source;
    },
    hideModal: (state) => {
      state.modal.error = null;
      state.modal.loading = false;
      state.modal.show = false;
      state.modal.object = null;
      state.modal.source = null;
    },
    hideQRGenerateModal: (state) => {
      state.qrGenerateModal.show = false;
    },
    hideReserveSaleModal: (state) => {
      state.reserveSaleModal.show = false;
      state.reserveSaleModal.error = null;
    },
    updateTableData: (state, payload) => {
      state.tableData[state.tableSource] = {...state.tableData[state.tableSource], ...payload}
      state.tableData[state.tableSource].rows = [...state.tableData[state.tableSource].rows]
    },
    updateTableLoading: (state, payload) => state.tableData[state.tableSource].loading = payload,
    updateUpcomingSales: (state, payload) => state.tableData[state.tableSource].upcomingSales = payload,
    updateInventory: (state, inventory) => {
      const index = state.tableData[state.tableSource].rows.findIndex(i => i.uuid === inventory.uuid);
      state.tableData[state.tableSource].rows[index] = {...state.tableData[state.tableSource].rows[index], ...inventory};
      state.tableData[state.tableSource].rows = [...state.tableData[state.tableSource].rows]
    },
    removeListInventoryImagesByUuids: (state, payload) => {
      const index = state.tableData[state.tableSource].rows.findIndex(i => i.uuid === payload.inventoryUuid);
      state.tableData[state.tableSource].rows[index].images = state.tableData[state.tableSource].rows[index].images.filter(img => !payload.uuidsToRemove.includes(img.uuid))
      state.tableData[state.tableSource].rows = [...state.tableData[state.tableSource].rows]
    },
    changeCurrentInventoryLoading: state => state.view.loading = !state.view.loading,
    setCurrentInventoryLoading: (state, loading) => {
      state.view.loading = !!loading;
    },
    updateViewInventory: (state, inventory) => state.view.currentInventory = inventory,
    updateImages: (state, images) => state.view.images = images,
    updateInventoryDashboardCustomer: (state, payload) => state.view.currentInventory.customer = {...state.view.currentInventory.customer, ...payload},
    updateSearchModel: (state, searchModel) => state.tableData[state.tableSource].searchModel = searchModel,
    showGoogleMapsModal: (state, payload) => {
      state.googleMapsModal.show = true
      state.googleMapsModal.params = payload
    },
    hideGoogleMapsModal: (state) => {
      state.googleMapsModal.show = false
      state.googleMapsModal.params = {}
    },
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData[state.tableSource].toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData[state.tableSource].toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData[state.tableSource].toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData[state.tableSource].toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
    resetSearchModel: state => state.tableData[state.tableSource].searchModel = new InventorySearch(),
    toggleContinuousAuctionBatchLotUpModalLoading: (state, payload) => state.continuousAuctionBatchLotUpModal.loading = payload,
    getDataForContinuousAuctionBatchLotUpModal: (state, payload) => {
      state.continuousAuctionBatchLotUpModal.items = payload
    },
    showContinuousAuctionBatchLotUpModal: (state, {source}) => {
      state.continuousAuctionBatchLotUpModal.show = true
      state.continuousAuctionBatchLotUpModal.source = source;
    },
    hideContinuousAuctionBatchLotUpModal: (state) => {
      state.continuousAuctionBatchLotUpModal.show = false
      state.continuousAuctionBatchLotUpModal.source = null;
    },
    showGroupItemsModal: (state, {source}) => {
      state.groupItemsModal.show = true;
      state.groupItemsModal.source = source;
    },
    hideGroupItemsModal: (state) => {
      state.groupItemsModal.show = false;
      state.groupItemsModal.source = null;
    },
    showGroupedItemsReserveModal: (state, {items, source}) => {
      state.groupedItemsReserveModal.show = true;
      state.groupedItemsReserveModal.items = items;
      state.groupedItemsReserveModal.source = source;
    },
    hideGroupedItemsReserveModal: (state) => {
      state.groupedItemsReserveModal.show = false;
      state.groupedItemsReserveModal.items = [];
      state.groupedItemsReserveModal.source = null;
    },
    showBulkEditModal: (state, {source}) => {
      state.bulkEditModal.show = true;
      state.bulkEditModal.source = source;
    },
    hideBulkEditModal: (state) => {
      state.bulkEditModal.show = false;
      state.bulkEditModal.error = null;
      state.bulkEditModal.source = null;
      state.bulkEditModal.items = [];
    },
    async inventoryBulkUpdate({commit}, payload) {
      return await InventoryService.inventoryBulkUpdate(payload);
    },
    showInventoryMiscChargeModal: (state, {object, source}) => {
      state.inventoryMiscChargeModal.object = object
      state.inventoryMiscChargeModal.source = source
      state.inventoryMiscChargeModal.show = true
    },
    hideInventoryMiscChargeModal: (state) => {
      state.inventoryMiscChargeModal.object = {}
      state.inventoryMiscChargeModal.source = null
      state.inventoryMiscChargeModal.show = false
    },
    showInventoryStatusesModal: (state, {source, inventory, statusKey}) => {
      state.inventoryStatusesModal.show = true
      state.inventoryStatusesModal.source = source
      state.inventoryStatusesModal.inventory = inventory
      state.inventoryStatusesModal.statusKey = statusKey
    },
    hideInventoryStatusesModal: (state) => {
      state.inventoryStatusesModal.show = false
      state.inventoryStatusesModal.source = null
      state.inventoryStatusesModal.inventory = null
      state.inventoryStatusesModal.statusKey = 'statuses'
    },
    showInventoryOverviewGenerationModal: (state, {source, payload}) => {
      state.inventoryOverviewGenerationModal.show = true
      state.inventoryOverviewGenerationModal.source = source
      state.inventoryOverviewGenerationModal.payload = payload
    },
    hideInventoryOverviewGenerationModal: (state) => {
      state.inventoryOverviewGenerationModal.show = false
      state.inventoryOverviewGenerationModal.source = null
      state.inventoryOverviewGenerationModal.payload = null
    },
    showCommissionsModal: (state, {source, object, inventory}) => {
      state.view.commissionsModal.show = true
      state.view.commissionsModal.source = source
      state.view.commissionsModal.object = object
      state.view.commissionsModal.inventory = inventory
    },
    hideCommissionsModal: (state) => {
      state.view.commissionsModal.show = false
      state.view.commissionsModal.source = null
      state.view.commissionsModal.object = null
      state.view.commissionsModal.inventory = null
    },
    showConditionalReportRequestModal: (state, {reservations}) => {
      state.conditionalReportRequestModal.show = true
      state.conditionalReportRequestModal.reservations = reservations
    },
    hideConditionalReportRequestModal: (state) => {
      state.conditionalReportRequestModal.show = false
      state.conditionalReportRequestModal.reservations = []
      state.conditionalReportRequestModal.object = {customer_uuid: null, customer: null}
    },
    updateConditionalReportRequestModalCustomer(state, payload) {
      let obj = _.cloneDeep(state.conditionalReportRequestModal.object);
      obj.customer_uuid = payload.uuid;
      obj.customer = payload;
      state.conditionalReportRequestModal.object = obj;
    },
    showTaxRateModal: (state, {source, object, inventory}) => {
      state.view.taxRateModal.show = true
      state.view.taxRateModal.source = source
      state.view.taxRateModal.object = object
      state.view.taxRateModal.inventory = inventory
    },
    hideTaxRateModal: (state) => {
      state.view.taxRateModal.show = false
      state.view.taxRateModal.source = null
      state.view.taxRateModal.object = null
      state.view.taxRateModal.inventory = null
    },
    showTransferAcrossBranchesModal: (state) => {
      state.transferAcrossBranchesModal.show = true;
    },
    hideTransferAcrossBranchesModal: (state) => {
      state.transferAcrossBranchesModal.show = false;
      state.transferAcrossBranchesModal.items = [];
      state.transferAcrossBranchesModal.branches = [];
    },
    showBatchUpdateReceiptModal: (state) => {
      state.batchUpdateReceiptModal.show = true;
    },
    hideBatchUpdateReceiptModal: (state) => {
      state.batchUpdateReceiptModal.show = false;
      state.batchUpdateReceiptModal.items = [];
    },
  },
  modules: {}
}
